import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Brain, 
  TrendingUp, 
  Home, 
  Clock, 
  Heart, 
  Users, 
  BarChart3,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  XCircle,
  ArrowRight
} from 'lucide-react';
import './WellnessRiskAssessment.css';

const WellnessRiskAssessment = () => {
  const [showUserForm, setShowUserForm] = useState(false);
  const [userInfo, setUserInfo] = useState({
    email: '',
    jobTitle: '',
    companyName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const categories = [
    {
      name: "Stress & Mental Health",
      icon: Brain,
      questions: [
        {
          id: "stress1",
          text: "Does your organization use real-time biometric monitoring to track employee stress levels?",
          whitematterValue: "Whitematter provides biometric tracking of stress levels"
        },
        {
          id: "stress2",
          text: "Do you have systems in place to detect burnout before it leads to absence?",
          whitematterValue: "WhiteMatter achieves 98% accuracy in detecting fatigue before it causes absence."
        },
        {
          id: "stress3",
          text: "Is mental health support easily accessible to all employees?",
          whitematterValue: "WhiteMatter data helps identify employees who need support before they reach crisis point."
        },
        {
          id: "stress4",
          text: "Do you measure the gap between perceived stress and actual biometric stress indicators?",
          whitematterValue: "WhiteMatter bridges the gap between what employees feel vs. biometric reality."
        }
      ]
    },
    {
      name: "Productivity & Performance",
      icon: TrendingUp,
      questions: [
        {
          id: "prod1",
          text: "Do you track the correlation between employee wellbeing and productivity?",
          whitematterValue: "WhiteMatter shows data-driven links between stress and performance (15-35% gains typical)."
        },
        {
          id: "prod2",
          text: "Can you identify productivity drops before they impact business outcomes?",
          whitematterValue: "WhiteMatter's early warning systems predict issues 2-4 weeks before escalation."
        },
        {
          id: "prod3",
          text: "Do you monitor absence rates and their correlation with wellbeing metrics?",
          whitematterValue: "WhiteMatter analytics reveal patterns between biometric stress and absence trends."
        }
      ]
    },
    {
      name: "Work Environment & Ergonomics",
      icon: Home,
      questions: [
        {
          id: "env1",
          text: "Have you conducted comprehensive workspace assessments for ergonomic risks?",
          whitematterValue: "WhiteMatter can detect physical stress patterns related to poor ergonomics."
        },
        {
          id: "env2",
          text: "Do you monitor environmental stressors (noise, lighting, temperature)?",
          whitematterValue: "WhiteMatter biometrics can correlate environmental factors with stress responses."
        },
        {
          id: "env3",
          text: "Are remote workers assessed for home office wellbeing risks?",
          whitematterValue: "WhiteMatter works seamlessly for remote workers, tracking stress regardless of location."
        }
      ]
    },
    {
      name: "Work-Life Balance",
      icon: Clock,
      questions: [
        {
          id: "balance1",
          text: "Do you monitor actual working hours vs. contracted hours?",
          whitematterValue: "WhiteMatter can detect stress patterns from overwork and poor work-life balance."
        },
        {
          id: "balance2",
          text: "Is flexible working genuinely available and utilized?",
          whitematterValue: "WhiteMatter data shows how flexibility impacts stress levels and recovery."
        },
        {
          id: "balance3",
          text: "Do employees take their full vacation entitlement?",
          whitematterValue: "WhiteMatter tracks recovery patterns and identifies employees who need time off."
        }
      ]
    },
    {
      name: "Physical Health & Safety",
      icon: Heart,
      questions: [
        {
          id: "health1",
          text: "Do you conduct regular health and safety risk assessments?",
          whitematterValue: "WhiteMatter provides objective health data to complement safety assessments."
        },
        {
          id: "health2",
          text: "Is fatigue monitoring in place for safety-critical roles?",
          whitematterValue: "WhiteMatter is essential for transport, healthcare, and manufacturing fatigue monitoring."
        },
        {
          id: "health3",
          text: "Are employees trained on recognizing stress and fatigue symptoms?",
          whitematterValue: "WhiteMatter provides objective data to validate and enhance training programs."
        }
      ]
    },
    {
      name: "Leadership & Culture",
      icon: Users,
      questions: [
        {
          id: "leader1",
          text: "Is wellbeing a visible priority for senior leadership?",
          whitematterValue: "WhiteMatter provides executives with data-driven insights to drive wellbeing strategy."
        },
        {
          id: "leader2",
          text: "Is there a dedicated budget for wellbeing initiatives?",
          whitematterValue: "WhiteMatter demonstrates ROI with 22% stress reduction."
        },
        {
          id: "leader3",
          text: "Do employees rate the organizational culture positively for wellbeing?",
          whitematterValue: "WhiteMatter objective data validates (or challenges) employee perception surveys."
        }
      ]
    },
    {
      name: "Data & Measurement",
      icon: BarChart3,
      questions: [
        {
          id: "data1",
          text: "Do you use analytics to measure wellbeing outcomes?",
          whitematterValue: "Organizations using WhiteMatter analytics see 25% better wellbeing outcomes."
        },
        {
          id: "data2",
          text: "Do you have early warning systems to predict wellbeing issues?",
          whitematterValue: "WhiteMatter predicts issues 2-4 weeks before escalation with 98% accuracy."
        }
      ]
    }
  ];

  // Initialize all answers to 0 on mount
  const [answers, setAnswers] = useState(() => {
    const initialAnswers = {};
    categories.forEach(category => {
      category.questions.forEach(question => {
        initialAnswers[question.id] = 0;
      });
    });
    return initialAnswers;
  });

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const calculateResults = () => {
    const totalQuestions = categories.reduce((sum, cat) => sum + cat.questions.length, 0);
    const totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
    const maxScore = totalQuestions * 10;
    const percentage = (totalScore / maxScore) * 100;

    let riskLevel, riskColor, riskIcon, riskDescription;
    
    if (percentage >= 80) {
      riskLevel = "Excellent Wellbeing";
      riskColor = "#34C759";
      riskIcon = CheckCircle;
      riskDescription = "Your organization demonstrates best practices in workplace wellbeing. Continue monitoring and optimizing with data-driven insights.";
    } else if (percentage >= 60) {
      riskLevel = "Moderate Risk";
      riskColor = "#FFD60A";
      riskIcon = AlertTriangle;
      riskDescription = "There are opportunities to improve wellbeing practices. Consider implementing biometric monitoring for early intervention.";
    } else if (percentage >= 40) {
      riskLevel = "High Risk";
      riskColor = "#FF9500";
      riskIcon = AlertCircle;
      riskDescription = "Significant wellbeing gaps detected. Immediate action recommended to prevent productivity loss and increased absence rates.";
    } else {
      riskLevel = "Critical Risk";
      riskColor = "#FF3B30";
      riskIcon = XCircle;
      riskDescription = "Critical wellbeing risks identified. Work-related stress is likely costing your organization significantly. Urgent intervention required.";
    }

    return {
      totalScore,
      maxScore,
      percentage: percentage.toFixed(1),
      riskLevel,
      riskColor,
      riskIcon,
      riskDescription
    };
  };

  const handleSubmit = () => {
    setShowUserForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUserInfoChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendResults = async () => {
    if (!userInfo.email || !userInfo.jobTitle || !userInfo.companyName) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const results = calculateResults();
      
      const response = await fetch('/api/send-assessment-results', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assessmentType: 'Wellness Risk Assessment',
          userInfo,
          results: {
            totalScore: results.totalScore,
            maxScore: results.maxScore,
            percentage: results.percentage,
            riskLevel: results.riskLevel,
            riskDescription: results.riskDescription
          }
        })
      });

      if (response.ok) {
        setShowUserForm(false);
        setShowSuccess(true);
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } else {
        alert('Failed to send results. Please try again.');
      }
    } catch (error) {
      console.error('Error sending results:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAssessment = () => {
    const initialAnswers = {};
    categories.forEach(category => {
      category.questions.forEach(question => {
        initialAnswers[question.id] = 0;
      });
    });
    setAnswers(initialAnswers);
    setShowUserForm(false);
    setUserInfo({ email: '', jobTitle: '', companyName: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="assessment-container">
      <Helmet>
        <title>Wellness Risk Assessment - Erasys</title>
        <meta name="description" content="Comprehensive workplace wellness risk assessment. Identify wellbeing gaps and opportunities for WhiteMatter biometric monitoring integration." />
      </Helmet>

      {/* Hero Section */}
      <section className="assessment-hero">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Wellness Risk Assessment</h1>
          <p>Evaluate your organization's workplace wellbeing practices across 7 critical categories</p>
          <div className="assessment-stats">
            <div className="stat-badge">
              <span className="stat-number">24</span>
              <span className="stat-label">Questions</span>
            </div>
            <div className="stat-badge">
              <span className="stat-number">7</span>
              <span className="stat-label">Categories</span>
            </div>
            <div className="stat-badge">
              <span className="stat-number">210</span>
              <span className="stat-label">Max Points</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Success Message */}
      {showSuccess && (
        <motion.section 
          className="success-section"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="success-card">
            <div className="success-icon">
              <CheckCircle size={64} />
            </div>
            <h2>Thank You!</h2>
            <p>Your assessment results have been sent to your email.</p>
            <p className="success-redirect">Redirecting to homepage...</p>
          </div>
        </motion.section>
      )}

      {/* User Info Form */}
      {showUserForm && !showSuccess && (
        <motion.section 
          className="user-form-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="user-form-card">
            <h2>Get Your Assessment Results</h2>
            <p>Please provide your information to receive your detailed wellness risk assessment results via email.</p>
            
            <div className="form-fields">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={userInfo.email}
                  onChange={(e) => handleUserInfoChange('email', e.target.value)}
                  placeholder="your.email@company.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="jobTitle">Job Title *</label>
                <input
                  type="text"
                  id="jobTitle"
                  value={userInfo.jobTitle}
                  onChange={(e) => handleUserInfoChange('jobTitle', e.target.value)}
                  placeholder="e.g., HR Manager"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="companyName">Company Name *</label>
                <input
                  type="text"
                  id="companyName"
                  value={userInfo.companyName}
                  onChange={(e) => handleUserInfoChange('companyName', e.target.value)}
                  placeholder="Your Company Name"
                  required
                />
              </div>
            </div>

            <button 
              className="btn-submit-info" 
              onClick={handleSendResults}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Results to Email'}
              {!isSubmitting && <ArrowRight size={20} />}
            </button>
          </div>
        </motion.section>
      )}

      {/* Assessment Form */}
      {!showUserForm && (
        <section className="assessment-form">
          {categories.map((category, catIndex) => {
            const CategoryIcon = category.icon;
            return (
              <motion.div
                key={catIndex}
                className="category-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: catIndex * 0.1 }}
              >
                <div className="category-header">
                  <CategoryIcon size={32} />
                  <h2>{category.name}</h2>
                  <span className="question-count">{category.questions.length} questions</span>
                </div>

                {category.questions.map((question, qIndex) => (
                  <div key={question.id} className="question-card">
                    <p className="question-text">
                      <span className="question-number">{qIndex + 1}.</span>
                      {question.text}
                    </p>
                    
                    <div className="slider-container">
                      <div className="slider-labels">
                        <span>No (0)</span>
                        <span>Partially (5)</span>
                        <span>Yes (10)</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="10"
                        value={answers[question.id] || 0}
                        onChange={(e) => handleAnswer(question.id, parseInt(e.target.value))}
                        className="slider"
                      />
                      <div className="slider-value">
                        Score: <strong>{answers[question.id] || 0}</strong>/10
                      </div>
                    </div>

                    <div className="whitematter-value">
                      <Brain size={16} />
                      <span><strong>WhiteMatter Value:</strong> {question.whitematterValue}</span>
                    </div>
                  </div>
                ))}
              </motion.div>
            );
          })}

          <motion.div
            className="submit-section"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <button className="btn-submit" onClick={handleSubmit}>
              <span>Calculate Risk Score</span>
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </section>
      )}

      {/* Info Section */}
      <section className="info-section">
        <h2>Risk Calculation Methodology</h2>
        <div className="methodology-grid">
          <div className="method-card">
            <h3>Scoring System</h3>
            <ul>
              <li>Each question: 0-10 points</li>
              <li>0 = highest risk</li>
              <li>10 = best practice</li>
              <li><strong>Total Maximum: 210 points</strong></li>
            </ul>
          </div>
          
          <div className="method-card">
            <h3>Risk Levels</h3>
            <div className="risk-level-item" style={{ borderLeftColor: '#34C759' }}>
              <strong>80-100%</strong> - Excellent Wellbeing
            </div>
            <div className="risk-level-item" style={{ borderLeftColor: '#FFD60A' }}>
              <strong>60-79%</strong> - Moderate Risk
            </div>
            <div className="risk-level-item" style={{ borderLeftColor: '#FF9500' }}>
              <strong>40-59%</strong> - High Risk
            </div>
            <div className="risk-level-item" style={{ borderLeftColor: '#FF3B30' }}>
              <strong>0-39%</strong> - Critical Risk
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WellnessRiskAssessment;
